import { Clipboard } from "@angular/cdk/clipboard";
import { Component, computed, effect, inject, Input, LOCALE_ID, OnDestroy, OnInit } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup } from "@angular/forms";
import { localizeRuntimeById } from "@app/runtime-localize";
import { UiKitModule } from "@app/ui-kit.module";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject } from "@domain/project/fgds-project";
import {
  createFgdsServicesFormValue,
  FGDS_SERVICE_DEFINITIONS,
  FgdsServiceKey,
  getFgdsCheckboxTestId,
  getFgdsCostsControlName,
  getFgdsNotesControlName,
  getFgdsNotesTestId,
  getFgdsPriceTestId,
  getRequiredFgdsProjectServicesWithPositions,
  mapFgdsServicesFormValue,
} from "@domain/project/fgds-service-definition";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProductData } from "@domain/project/product-data/product-data";
import {
  applyAdditionalProductDrafts,
  createAdditionalProductDrafts,
  createAdditionalProductRollbackState,
  getChangedAdditionalProductSections,
  getVisibleAdditionalProductSections,
  normalizeAdditionalProductQuantity,
  rollbackAdditionalProductDrafts,
} from "@domain/project/product-list/additional-products-display.util";
import { FgdsProductListAddService } from "@domain/project/product-list/fgds-product-list-add.service";
import { GroupSection } from "@domain/project/product-list/group-section";
import {
  createProductListDiscountKey,
  ProductListDiscountKey,
} from "@domain/project/product-list/product-list-discount-key.util";
import { normalizeProductListDiscountInput } from "@domain/project/product-list/product-list-discount.util";
import {
  AdditionalCategoryDisplayData,
  buildFgdsAdditionalCategoryDisplayData,
  buildFgdsPlacedGroupDisplayData,
  FgdsSectionDisplayData,
  ProductListAttachmentInstructionsDisplayData,
  ProductListGroupDisplayData,
  ProductListProductSectionDisplayData,
} from "@domain/project/product-list/product-list-display-data.util";
import { AdditionalProductConfigSection, ProductListService } from "@domain/project/product-list/product-list.service";
import { ProjectCostService } from "@domain/project/product-list/project-cost.service";
import { ProjectService } from "@domain/project/project.service";
import { TemplateDto } from "@domain/project/template";
import { TemplateService } from "@domain/project/template/template.service";
import { UserService } from "@domain/user/user.service";
import { AdditionalProductsTableComponent } from "@project/project-details/product-list/additional-products-table/additional-products-table.component";
import { ClipboardProductListService } from "@project/project-details/product-list/clipboard-product-list.service";
import { FgdsProductListSearchComponent } from "@project/project-details/product-list/fgds-product-list-search/fgds-product-list-search.component";
import { ProductTableAttachmentInstructionsComponent } from "@project/project-details/product-list/product-table-attachment-instructions/product-table-attachment-instructions.component";
import { ProductTableComponent } from "@project/project-details/product-list/product-table/product-table.component";
import { ServiceEditorEntry, ServicesEditorComponent } from "@project/project-details/services-editor/services-editor.component";
import { CountryCurrencyMapper } from "@utils/country-currency-mapper";
import { ToastHelper } from "@utils/toast-helper.service";
import { combineLatest, distinctUntilChanged, EMPTY, filter, forkJoin, map, Observable, of, Subscription, switchMap } from "rxjs";

enum ProductListEditSection {
  PRODUCTS_ON_FLOOR_PLAN = "PRODUCTS_ON_FLOOR_PLAN",
  ADDITIONAL_PRODUCTS = "ADDITIONAL_PRODUCTS",
  ATTACHMENT_INSTRUCTIONS = "ATTACHMENT_INSTRUCTIONS",
}

@Component({
  selector: "app-product-list",
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.scss"],
  imports: [
    UiKitModule,
    ProductTableComponent,
    ProductTableAttachmentInstructionsComponent,
    AdditionalProductsTableComponent,
    ServicesEditorComponent,
    FgdsProductListSearchComponent,
  ],
  standalone: true,
})
export class ProductListComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private formBuilder = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private projectCostService = inject(ProjectCostService);
  private productService = inject(ProductService);
  private productListService = inject(ProductListService);
  private countryCurrencyMapper = inject(CountryCurrencyMapper);
  private toastHelper = inject(ToastHelper);
  private fgdsProductListAddService = inject(FgdsProductListAddService);
  protected readonly locale = inject(LOCALE_ID);
  private templateService = inject(TemplateService);
  private clipboard = inject(Clipboard);
  private clipboardProductListService = inject(ClipboardProductListService);
  protected readonly productListEditSection = ProductListEditSection;

  @Input() set projectData(project: FgdsProject) {
    this.productListDataInitialized = false;
    this.project = project;
    this.resetComponentState();
    this.initServiceForm();
    if (this.initialized) {
      this.refreshProductList();
      return;
    }
    this.groupSections = [];
    this.additionalSections = [];
    this.serviceRows = [];
  }

  project!: FgdsProject;
  productListDataInitialized: boolean = false;

  placedProductsSubtotal: number = 0;
  allProductsSubtotal: number = 0;
  projectTotalNetCosts: number | undefined;

  servicesForm!: FormGroup;
  groupSections: GroupSection[] = [];
  additionalSections: AdditionalProductConfigSection[] = [];
  editProductList = false;
  private editingPlacedSectionKey: string | undefined;
  additionalProductDrafts: Record<string, number> = {};
  private discountDrafts: Partial<Record<ProductListEditSection, Record<string, number>>> = {};
  private discountSnapshots: Partial<Record<ProductListEditSection, Record<string, number>>> = {};
  private discountKeys: Partial<Record<ProductListEditSection, Record<string, ProductListDiscountKey>>> = {};
  private initialized = false;

  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected readonly canEditProductList = toSignal(
    combineLatest([
      this.userService.user$.pipe(map((user) => user.readOnlyAccess)),
      this.projectService.hasUserWritePermission$,
    ]).pipe(map(([isReadOnlyUser, hasWritePermission]) => !isReadOnlyUser && hasWritePermission === true)),
    { initialValue: false },
  );
  protected readonly showZrefPrices = toSignal(this.userService.user$.pipe(map((user) => user.showZrefPrices)), {
    initialValue: false,
  });
  protected get currencyPlaceholderText(): string {
    return $localize`:@@productList.priceForm.placeholder:Preis in ${this.setLocalCurrency()}`;
  }
  protected readonly editAdditionalProductsLabel = $localize`:@@global.edit:Bearbeiten`;
  protected readonly saveAdditionalProductsLabel = $localize`:@@global.save:Speichern`;
  protected readonly cancelProductListEditLabel = $localize`:@@global.cancel:Abbrechen`;
  protected readonly additionalProductsLabel = $localize`:@@productList.additionalProducts:Zusätzliche Produkte`;
  protected readonly attachmentInstructionsLabel = localizeRuntimeById(
    "productList.attachmentInstructions",
    "Gebrauchsanweisungen",
  );
  protected assemblyPosition: number = 0;
  protected installationPosition: number = 0;
  protected documentationPosition: number = 0;
  protected engineeringPosition: number = 0;
  protected additionalServicesPosition: number = 0;
  protected projectAttachmentInstructions: ProductData[] = [];
  protected projectAttachmentInstructionsPrice: number = 0;
  protected visibleProjectAttachmentInstructions: ProductData[] = [];
  protected visibleProjectAttachmentInstructionsPrice: number = 0;
  protected additionalProductsSubtotal: number = 0;
  protected placedGroupDisplayData: ProductListGroupDisplayData<
    FgdsSectionDisplayData,
    ProductListAttachmentInstructionsDisplayData,
    GroupSection
  >[] = [];
  protected additionalCategoryDisplayData: AdditionalCategoryDisplayData<ProductData>[] = [];
  protected productsOnFloorPlanExpanded = true;
  protected attachmentInstructionsExpanded = true;
  protected additionalProductsExpanded = true;
  protected servicesExpanded = true;
  protected serviceRows: {
    key: FgdsServiceKey;
    position: number;
    label: string;
    partNumber: string;
    notes: string;
    costs?: number;
  }[] = [];

  private servicesFormSubscription?: Subscription;
  private groupSectionsSubscription?: Subscription;
  private productRefreshSubscription?: Subscription;
  private productAddedSubscription?: Subscription;
  private groupExpandedState: Record<string, boolean> = {};
  private productSectionExpandedState: Record<string, boolean> = {};
  private additionalCategoryExpandedState: Record<string, boolean> = {};

  private template$ = combineLatest([this.templateService.projectTemplate$, this.userService.businessScope$]).pipe(
    switchMap(([projectTemplate, scope]) => {
      if (!scope) return EMPTY;
      return projectTemplate ? of(projectTemplate) : this.templateService.getGlobalTemplate(scope);
    }),
    filter((template): template is TemplateDto => !!template),
  );

  protected readonly template = toSignal(this.template$, {
    initialValue: null,
  });

  protected isCopyToClipboardVisible = computed<boolean | undefined>(() => {
    const template = this.template();
    return template?.properties?.project?.productList?.copyToClipboard?.visible;
  });

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(
      map((user) => user.readOnlyAccess),
      distinctUntilChanged(),
    );
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );

    effect(() => {
      this.showZrefPrices();
      this.refreshSummaryTotals();
    });
  }

  ngOnInit(): void {
    this.initialized = true;
    this.productRefreshSubscription = this.productService.refreshedData$.subscribe((countries) => {
      const currentCountry = this.productService.getFilterCountry();
      if (!this.project || !currentCountry || !countries.includes(currentCountry)) {
        return;
      }

      this.refreshProductList(true);
    });
    this.productAddedSubscription = this.fgdsProductListAddService.productAdded$.subscribe(() => {
      this.refreshProductList(true);
    });
    this.refreshProductList();
  }

  ngOnDestroy(): void {
    this.servicesFormSubscription?.unsubscribe();
    this.groupSectionsSubscription?.unsubscribe();
    this.productRefreshSubscription?.unsubscribe();
    this.productAddedSubscription?.unsubscribe();
  }

  private refreshProductList(preserveVisibleContent = false) {
    if (!preserveVisibleContent) {
      this.productListDataInitialized = false;
      this.serviceRows = [];
    }

    this.groupSectionsSubscription?.unsubscribe();
    this.groupSectionsSubscription = this.initGroupSections().subscribe(({ groupSections, additionalSections }) => {
      this.groupSections = groupSections;
      this.additionalSections = additionalSections;
      this.projectAttachmentInstructions = this.buildProjectAttachmentInstructions(this.groupSections, this.additionalSections);
      this.projectAttachmentInstructionsPrice = this.projectCostService.calculateTotalAttachmentInstructionsPrice(
        this.projectAttachmentInstructions,
        this.showZrefPrices(),
      );
      this.refreshServiceRows();
      this.refreshViewModels();
      this.productListDataInitialized = true;
    });
  }

  protected calcServiceSubtotal(): number {
    return this.projectCostService.calculateServicesCosts(this.project);
  }

  private readonly ZREF_CURRENCY = "EUR";

  protected setCurrency() {
    if (this.showZrefPrices()) return this.ZREF_CURRENCY;
    return this.countryCurrencyMapper.getCurrency(this.productService.getFilterCountry()!);
  }

  protected setLocalCurrency() {
    return this.countryCurrencyMapper.getCurrency(this.productService.getFilterCountry()!);
  }

  protected areCurrenciesMixed(): boolean {
    if (!this.showZrefPrices()) return false;
    return this.setLocalCurrency() !== this.ZREF_CURRENCY;
  }

  protected get serviceEditorEntries(): ServiceEditorEntry[] {
    return FGDS_SERVICE_DEFINITIONS.map((definition) => ({
      id: definition.key,
      checkboxControlName: definition.key,
      checkboxLabel: definition.label(),
      notesControlName: getFgdsNotesControlName(definition.key),
      checkboxTestId: getFgdsCheckboxTestId(definition.key),
      notesTestId: getFgdsNotesTestId(definition.key),
      priceControlName: getFgdsCostsControlName(definition.key),
      priceTestId: getFgdsPriceTestId(definition.key),
    }));
  }

  private initServiceForm() {
    const formValue = createFgdsServicesFormValue(this.project);
    if (!this.servicesForm) {
      this.servicesForm = this.formBuilder.group(formValue);
      this.servicesFormSubscription = this.servicesForm.valueChanges.subscribe(() => {
        this.mapServices();
        this.projectService.update(this.project).subscribe();
        this.refreshSummaryTotals();
        this.refreshServiceRows();
      });
      return;
    }

    this.servicesForm.patchValue(formValue, { emitEvent: false });
  }

  private mapServices() {
    mapFgdsServicesFormValue(this.project, this.servicesForm.value);
    this.project.lastModified = new Date();
  }

  private refreshServiceRows() {
    this.serviceRows = getRequiredFgdsProjectServicesWithPositions(
      this.project,
      this.productListService.getLatestPositionIdForProducts(this.groupSections, this.additionalSections),
    ).map(({ definition, service, position }) => ({
      key: definition.key,
      position,
      label: definition.label(),
      partNumber: definition.partNumber,
      notes: service.notes,
      costs: service.costs,
    }));
  }

  private initGroupSections() {
    return forkJoin([
      this.productListService.createGroupSectionsForProject(this.project),
      this.productListService.createAdditionalConfigSections(this.project),
    ]).pipe(
      map(([groupSections, additionalSections]) => ({
        groupSections,
        additionalSections,
      })),
    );
  }

  protected copyProductList() {
    const productData = this.clipboardProductListService.build(
      this.groupSections,
      this.additionalSections,
      this.projectAttachmentInstructions,
      this.serviceRows,
      this.showZrefPrices() ?? false,
    );
    const success = this.productListDataInitialized && this.clipboard.copy(productData);

    this.toastHelper[success ? "success" : "error"](
      success
        ? $localize`:@@toast.project.copyToClipboard.success: Die Produktliste wurde in die Zwischenablage kopiert`
        : $localize`:@@toast.project.copyToClipboard.error: Die Produktliste konnte nicht in die Zwischenablage kopiert werden.`,
    );
  }

  protected startProductListEdit() {
    if (!this.canEditProductList() || this.editProductList) {
      return;
    }

    this.additionalProductDrafts = createAdditionalProductDrafts(this.additionalSections);
    this.beginDiscountEdit(ProductListEditSection.PRODUCTS_ON_FLOOR_PLAN);
    this.beginDiscountEdit(ProductListEditSection.ADDITIONAL_PRODUCTS);
    this.beginDiscountEdit(ProductListEditSection.ATTACHMENT_INSTRUCTIONS);
    this.editProductList = true;
    this.refreshAdditionalProductsViewModels();
  }

  protected saveProductListEdit() {
    if (!this.editProductList) {
      return;
    }

    const changedAdditionalSections = getChangedAdditionalProductSections(this.additionalSections, this.additionalProductDrafts);
    const changedDiscountEntries = Object.values(ProductListEditSection).flatMap((section) =>
      this.getChangedDiscountEntries(section),
    );

    if (changedAdditionalSections.length === 0 && changedDiscountEntries.length === 0) {
      this.finishProductListEdit();
      return;
    }

    const quantityRollbackState = createAdditionalProductRollbackState(changedAdditionalSections);
    applyAdditionalProductDrafts(changedAdditionalSections, this.additionalProductDrafts);
    this.applyDiscountEntriesToProject(changedDiscountEntries);

    this.projectService.update(this.project).subscribe({
      next: () => {
        const hasQuantityChanges = changedAdditionalSections.length > 0;
        this.finishProductListEdit();
        this.refreshProductList(true);
        if (hasQuantityChanges) {
          this.toastHelper.success(
            $localize`:@@toast.additionalProducts.saved.header:Speichern erfolgreich`,
            $localize`:@@toast.additionalProducts.saved.message:Die zusätzlichen Produkte wurden erfolgreich gespeichert.`,
          );
        }
      },
      error: () => {
        rollbackAdditionalProductDrafts(changedAdditionalSections, quantityRollbackState);
        this.restoreAllDiscountSnapshots();
        this.finishProductListEdit();
        this.refreshProductList(true);
        this.toastHelper.error(
          changedAdditionalSections.length > 0
            ? $localize`:@@toast.additionalProducts.saveFailed.message:Die zusätzlichen Produkte konnten nicht gespeichert werden.`
            : $localize`:@@toast.productList.discount.saveFailed:Der Rabatt konnte nicht gespeichert werden. Der vorherige Wert wurde wiederhergestellt.`,
        );
      },
    });
  }

  protected cancelProductListEdit() {
    if (!this.editProductList) {
      return;
    }

    this.restoreAllDiscountSnapshots();
    this.finishProductListEdit();
    this.refreshProductList(true);
  }

  protected updateAdditionalProductQuantity(configId: string, delta: number) {
    const currentQuantity = this.additionalProductDrafts[configId] ?? 0;
    this.additionalProductDrafts[configId] = normalizeAdditionalProductQuantity(currentQuantity + delta);
    this.refreshAdditionalProductsViewModels();
  }

  protected setAdditionalProductQuantity(configId: string, quantity: number | string) {
    this.additionalProductDrafts[configId] = normalizeAdditionalProductQuantity(quantity);
    this.refreshAdditionalProductsViewModels();
  }

  protected removeAdditionalProduct(configId: string) {
    this.additionalProductDrafts[configId] = 0;
    this.refreshAdditionalProductsViewModels();
  }

  protected updateProductListDiscount(
    change: { key: ProductListDiscountKey; value: string },
    section: ProductListEditSection,
    placedSectionKey?: string,
  ): void {
    if (!this.canEditProductList()) {
      return;
    }

    const discount = normalizeProductListDiscountInput(change.value);
    if (discount === undefined) {
      this.toastHelper.error(
        $localize`:@@toast.productList.discount.invalid:Bitte geben Sie einen Rabatt zwischen 0 und 100 % ein.`,
      );
      return;
    }

    const drafts = this.discountDrafts[section];
    if (!drafts) {
      return;
    }

    const serializedKey = createProductListDiscountKey(change.key);
    const snapshots = this.discountSnapshots[section];
    if (snapshots && !(serializedKey in snapshots)) {
      snapshots[serializedKey] = this.project.getProductListDiscount(change.key);
    }
    drafts[serializedKey] = discount;
    this.discountKeys[section] ??= {};
    this.discountKeys[section]![serializedKey] = change.key;
    this.applyDiscountToRows(section, change.key, discount, placedSectionKey);
    this.groupSections.forEach((groupSection) => this.productListService.recalculateGroupSectionPrices(groupSection));
    if (section === ProductListEditSection.ADDITIONAL_PRODUCTS) {
      this.refreshAdditionalProductsViewModels();
    } else {
      this.refreshSummaryTotals();
    }
  }

  private beginDiscountEdit(section: ProductListEditSection, placedSectionKey?: string) {
    const snapshot: Record<string, number> = {};
    const keys: Record<string, ProductListDiscountKey> = {};
    this.getProductsForEditSection(section, placedSectionKey)
      .filter((product): product is ProductData | PlaceholderProduct => !!product)
      .forEach((product) => {
        if (product.discountKey) {
          const serializedKey = createProductListDiscountKey(product.discountKey);
          keys[serializedKey] = product.discountKey;
          snapshot[serializedKey] = this.project.getProductListDiscount(product.discountKey);
        }
      });
    this.discountSnapshots[section] = snapshot;
    this.discountDrafts[section] = { ...snapshot };
    this.discountKeys[section] = keys;
  }

  private finishProductListEdit() {
    this.additionalProductDrafts = {};
    Object.values(ProductListEditSection).forEach((section) => this.clearDiscountEdit(section));
    this.editProductList = false;
    this.editingPlacedSectionKey = undefined;
    this.refreshAdditionalProductsViewModels();
  }

  private restoreAllDiscountSnapshots() {
    Object.values(ProductListEditSection).forEach((section) => this.restoreDiscountSnapshot(section));
  }

  private clearDiscountEdit(section: ProductListEditSection) {
    delete this.discountDrafts[section];
    delete this.discountSnapshots[section];
    delete this.discountKeys[section];
  }

  private restoreDiscountSnapshot(section: ProductListEditSection) {
    const snapshot = this.discountSnapshots[section];
    if (!snapshot) {
      return;
    }

    this.getProductsForEditSection(section, this.editingPlacedSectionKey)
      .filter((product): product is ProductData | PlaceholderProduct => !!product)
      .forEach((product) => {
        if (product.discountKey) {
          const serializedKey = createProductListDiscountKey(product.discountKey);
          const discount = snapshot[serializedKey] ?? 0;
          product.discount = discount;
          this.project.setProductListDiscount(product.discountKey, discount);
        }
      });
    this.clearDiscountEdit(section);
  }

  private applyDiscountToRows(
    section: ProductListEditSection,
    key: ProductListDiscountKey,
    discount: number,
    placedSectionKey?: string,
  ) {
    const serializedKey = createProductListDiscountKey(key);
    this.getProductsForEditSection(section, placedSectionKey ?? this.editingPlacedSectionKey)
      .filter((product): product is ProductData | PlaceholderProduct => !!product)
      .forEach((product) => {
        if (product.discountKey && createProductListDiscountKey(product.discountKey) === serializedKey) {
          product.discount = discount;
        }
      });
  }

  private getDiscountKeyForSerializedKey(section: ProductListEditSection, serializedKey: string) {
    return this.discountKeys[section]?.[serializedKey];
  }

  private getProductsForEditSection(
    section: ProductListEditSection,
    placedSectionKey?: string,
  ): Array<ProductData | PlaceholderProduct> {
    if (section === ProductListEditSection.ATTACHMENT_INSTRUCTIONS) {
      return this.projectAttachmentInstructions;
    }

    if (section === ProductListEditSection.ADDITIONAL_PRODUCTS) {
      return this.additionalSections.flatMap((additionalSection) =>
        additionalSection.productSection.mappings.flatMap((mapping) => [
          ...mapping.data,
          ...(mapping.attachmentInstructions ?? []),
        ]),
      );
    }

    if (placedSectionKey) {
      const [groupKey, sectionKey] = placedSectionKey.split(":");
      const section = this.placedGroupDisplayData
        .find((group) => group.key === groupKey)
        ?.sections.find((candidate) => candidate.key === sectionKey);

      return section && this.isProductSection(section)
        ? [
            ...section.section.mappings.flatMap((mapping) => [...mapping.data, ...(mapping.attachmentInstructions ?? [])]),
            ...(section.section.devices ?? []).flatMap((device) => device.products),
            ...(section.section.placeholders ?? []).flatMap((placeholder) => placeholder.products),
          ]
        : [];
    }

    return this.groupSections.flatMap((group) =>
      [
        group.alarmDevices,
        group.gasWarningCenters,
        group.plasticSigns,
        group.signalElements,
        group.transmitters,
        group.flameDetectors,
      ]
        .filter((productSection) => !!productSection)
        .flatMap((productSection) => [
          ...productSection.mappings.flatMap((mapping) => [...mapping.data, ...(mapping.attachmentInstructions ?? [])]),
          ...(productSection.devices ?? []).flatMap((device) => device.products),
          ...(productSection.placeholders ?? []).flatMap((placeholder) => placeholder.products),
        ]),
    );
  }

  private getChangedDiscountEntries(section: ProductListEditSection): Array<[ProductListDiscountKey, number]> {
    const drafts = this.discountDrafts[section] ?? {};
    const snapshots = this.discountSnapshots[section] ?? {};

    return Object.entries(drafts)
      .filter(([serializedKey, discount]) => snapshots[serializedKey] !== discount)
      .map(([serializedKey, discount]) => {
        const key = this.discountKeys[section]?.[serializedKey];
        return key ? [key, discount] : undefined;
      })
      .filter((entry): entry is [ProductListDiscountKey, number] => !!entry);
  }

  private applyDiscountEntriesToProject(entries: Array<[ProductListDiscountKey, number]>) {
    entries.forEach(([key, discount]) => this.project.setProductListDiscount(key, discount));
  }

  protected getAdditionalProductsEmptyMessage(categoryLabel: string) {
    return $localize`:@@productList.additionalProducts.emptyAfterEdit.category:Es wurden noch keine zusätzlichen ${categoryLabel}:category: hinzugefügt.`;
  }

  protected isPlacedSectionEditing(groupKey: string, sectionKey: string): boolean {
    if (!this.editProductList) {
      return false;
    }
    return this.editingPlacedSectionKey === this.getPlacedSectionEditKey(groupKey, sectionKey) || !this.editingPlacedSectionKey;
  }

  protected getPlacedSectionEditKey(groupKey: string, sectionKey: string): string {
    return `${groupKey}:${sectionKey}`;
  }

  protected getGroupExpanded(groupName: string) {
    return this.groupExpandedState[groupName] ?? true;
  }

  protected setGroupExpanded(groupName: string, expanded: boolean) {
    this.groupExpandedState[groupName] = expanded;
  }

  protected getProductSectionExpanded(groupName: string, sectionName: string) {
    return this.productSectionExpandedState[this.getSectionStateKey(groupName, sectionName)] ?? true;
  }

  protected setProductSectionExpanded(groupName: string, sectionName: string, expanded: boolean) {
    this.productSectionExpandedState[this.getSectionStateKey(groupName, sectionName)] = expanded;
  }

  protected getAdditionalCategoryExpanded(categoryKey: string) {
    return this.additionalCategoryExpandedState[categoryKey] ?? true;
  }

  protected setAdditionalCategoryExpanded(categoryKey: string, expanded: boolean) {
    this.additionalCategoryExpandedState[categoryKey] = expanded;
  }

  protected isAttachmentInstructionsSection(
    section: ProductListGroupDisplayData<
      FgdsSectionDisplayData,
      ProductListAttachmentInstructionsDisplayData,
      GroupSection
    >["sections"][number],
  ): section is ProductListAttachmentInstructionsDisplayData {
    return "attachmentInstructions" in section;
  }

  protected isProductSection(
    section: ProductListGroupDisplayData<
      FgdsSectionDisplayData,
      ProductListAttachmentInstructionsDisplayData,
      GroupSection
    >["sections"][number],
  ): section is ProductListProductSectionDisplayData<FgdsSectionDisplayData> {
    return "section" in section;
  }

  private getSectionStateKey(groupName: string, sectionName: string) {
    return `${groupName}:${sectionName}`;
  }

  private confirmAdditionalProductsSave() {
    const changedSections = getChangedAdditionalProductSections(this.additionalSections, this.additionalProductDrafts);
    const changedDiscountEntries = this.getChangedDiscountEntries(ProductListEditSection.ADDITIONAL_PRODUCTS);
    if (changedSections.length === 0 && changedDiscountEntries.length === 0) {
      this.finishProductListEditSection(ProductListEditSection.ADDITIONAL_PRODUCTS);
      return;
    }

    const previousQuantities = createAdditionalProductRollbackState(changedSections);
    applyAdditionalProductDrafts(changedSections, this.additionalProductDrafts);
    this.applyDiscountEntriesToProject(changedDiscountEntries);
    this.projectService.update(this.project).subscribe({
      next: () => {
        this.finishProductListEditSection(ProductListEditSection.ADDITIONAL_PRODUCTS);
        this.refreshProductList(true);
        this.toastHelper.success(
          $localize`:@@toast.additionalProducts.saved.header:Speichern erfolgreich`,
          $localize`:@@toast.additionalProducts.saved.message:Die zusätzlichen Produkte wurden erfolgreich gespeichert.`,
        );
      },
      error: () => {
        rollbackAdditionalProductDrafts(changedSections, previousQuantities);
        this.restoreDiscountSnapshot(ProductListEditSection.ADDITIONAL_PRODUCTS);
        this.additionalProductDrafts = createAdditionalProductDrafts(this.additionalSections);
        this.refreshAdditionalProductsViewModels();
        this.toastHelper.error(
          $localize`:@@toast.additionalProducts.saveFailed.header:Speichern fehlgeschlagen`,
          $localize`:@@toast.additionalProducts.saveFailed.message:Die zusätzlichen Produkte konnten nicht gespeichert werden.`,
        );
      },
    });
  }

  private finishProductListEditSection(section: ProductListEditSection) {
    this.additionalProductDrafts = {};
    this.clearDiscountEdit(section);
    this.refreshAdditionalProductsViewModels();
  }

  private refreshViewModels() {
    this.placedGroupDisplayData = buildFgdsPlacedGroupDisplayData(
      this.groupSections,
      (group) => this.productListService.getAttachmentInstructionsForGroup(group),
      (instructions) => this.projectCostService.calculateTotalAttachmentInstructionsPrice(instructions),
    )
      .map((group) => ({
        ...group,
        sections: group.sections.filter((section) => !this.isAttachmentInstructionsSection(section)),
      }))
      .filter((group) => group.sections.length > 0);
    this.refreshAdditionalProductsViewModels();
  }

  private refreshAdditionalProductsViewModels() {
    this.additionalCategoryDisplayData = buildFgdsAdditionalCategoryDisplayData(
      this.editProductList,
      this.additionalSections,
      this.additionalProductDrafts,
    );

    const visibleAdditionalSections = getVisibleAdditionalProductSections(
      this.editProductList,
      this.additionalSections,
      this.additionalProductDrafts,
    );
    this.visibleProjectAttachmentInstructions = this.buildProjectAttachmentInstructions(
      this.groupSections,
      this.editProductList ? visibleAdditionalSections : this.additionalSections,
    );
    this.visibleProjectAttachmentInstructionsPrice = this.editProductList
      ? this.projectCostService.calculateTotalAttachmentInstructionsPrice(
          this.visibleProjectAttachmentInstructions,
          this.showZrefPrices(),
        )
      : this.projectAttachmentInstructionsPrice;
    this.refreshSummaryTotals();
  }

  private resetComponentState() {
    this.editProductList = false;
    this.editingPlacedSectionKey = undefined;
    this.additionalProductDrafts = {};
    this.discountDrafts = {};
    this.discountSnapshots = {};
    this.discountKeys = {};
    this.productsOnFloorPlanExpanded = true;
    this.attachmentInstructionsExpanded = true;
    this.additionalProductsExpanded = true;
    this.servicesExpanded = true;
    this.groupExpandedState = {};
    this.productSectionExpandedState = {};
    this.additionalCategoryExpandedState = {};
  }

  private refreshSummaryTotals() {
    const useZref = this.showZrefPrices();
    const placedProductsSubtotal = this.groupSections.reduce(
      (total, groupSection) => total + (useZref ? groupSection.zrefSubTotal : groupSection.subTotal),
      0,
    );
    const additionalProductsSubtotal = this.additionalCategoryDisplayData.reduce(
      (total, category) => total + ((useZref ? category.zrefSubTotal : category.subtotal) ?? 0),
      0,
    );
    const attachmentInstructionsPrice = this.projectCostService.calculateTotalAttachmentInstructionsPrice(
      this.visibleProjectAttachmentInstructions,
      useZref,
    );

    this.visibleProjectAttachmentInstructionsPrice = attachmentInstructionsPrice;
    this.placedProductsSubtotal = placedProductsSubtotal;
    this.additionalProductsSubtotal = additionalProductsSubtotal;
    this.allProductsSubtotal = placedProductsSubtotal + additionalProductsSubtotal + attachmentInstructionsPrice;
    this.projectTotalNetCosts = this.areCurrenciesMixed() ? undefined : this.allProductsSubtotal + this.calcServiceSubtotal();
  }

  private buildProjectAttachmentInstructions(
    groupSections: GroupSection[],
    additionalSections: AdditionalProductConfigSection[],
  ) {
    return this.productListService.getRootAttachmentInstructions(groupSections, additionalSections);
  }
}
